import React from 'react';
import {I18n} from 'react-redux-i18n';

import {CellProps, Row} from 'react-table';
import {useSelector} from 'react-redux';
// eslint-disable-next-line max-len
import {LastOperationStatusIndicator} from '../LastOperationStatusIndicator/LastOperationStatusIndicator';
import {EntitiesListWithViewModesCard, Switch} from '../index';

import {ACCESS_RIGHTS} from '../../constants';
import {DataExportModel} from '../../types/DataExport';
import {Organization} from '../../types/Organization';

interface DataExportTableProps {
  handleClickButtonNew: () => void;
  // eslint-disable-next-line no-unused-vars
  handleClickRow: (index: number) => void;
  isEditMode: boolean;
  mode: string;
  selectedDataExportIndex: number;
  tableData?: Array<DataExportModel>;
}

const rightAvailabilityMap = {
  buttonCreate: [ACCESS_RIGHTS.DATA_EXPORT_CREATE],
};

function DataExportTable({
  handleClickButtonNew,
  handleClickRow,
  mode,
  isEditMode,
  selectedDataExportIndex,
  tableData = [],
}: DataExportTableProps) {
  const userOrgs: Organization[] = useSelector(
    (store: any) => store.userOrganizationsReducer.userOrganizations,
  );
  const accountInfo = useSelector((store: any) => store.operatorLayoutReducer.accountInfo);

  const checkedCellRenderer = (row: CellProps<DataExportModel>) => (
    <Switch
      isChecked={row.value}
      disabled={true}
      switchClassName="switch_td"
    />
  );

  const orgsRenderer = (row: CellProps<DataExportModel> & Row<DataExportModel>) => {
    if (userOrgs.length === 0) {
      return '-';
    }
    const selectedOrgs = userOrgs.filter((org) => row.value.includes(org.id));

    if (row.original.allOrgs) {
      return I18n.t('dataExportPage.table.allOrgs');
    }

    let orgsText = selectedOrgs && selectedOrgs.length > 0 ? selectedOrgs[0].name : '';
    if (selectedOrgs.length > 1) {
      orgsText += ` (+${selectedOrgs.length - 1})`;
    }
    return orgsText;
  };

  const statusRenderer = (row: any) => {
    const status = row.original.status;
    const operation = row.original.lastOperation;

    return (
      <LastOperationStatusIndicator
        operation={operation}
        status={status}
      />
    );
  };

  const headersFullWidthMode = [
    {
      Header: I18n.t('dataExportPage.table.headers.exportName'),
      accessor: 'name',
      sortable: false,
      filterable: false,
    },
    {
      Header: I18n.t('dataExportPage.table.headers.type'),
      accessor: 'streamTypeName',
      sortable: false,
      filterable: false,
    },
    accountInfo?.organizationsAllowed && {
      Header: I18n.t('dataExportPage.table.headers.organizations'),
      accessor: 'organizationIds',
      Cell: orgsRenderer,
      sortable: false,
      filterable: false,
    },
    {
      Header: I18n.t('dataExportPage.table.headers.destinations'),
      accessor: 'collectorUrl',
      sortable: false,
      filterable: false,
    },
    {
      Header: I18n.t('dataExportPage.table.headers.enabled'),
      accessor: 'enabled',
      Cell: checkedCellRenderer,
      sortable: false,
      filterable: false,
    },
    {
      Header: I18n.t('dataExportPage.table.headers.status'),
      accessor: 'status',
      Cell: statusRenderer,
      sortable: false,
      filterable: false,
    },
  ].filter(Boolean);

  const headersCompressedMode = [
    {
      Header: null,
      accessor: 'name',
      sortable: false,
      filterable: false,
    },
  ];

  return (
    <EntitiesListWithViewModesCard
      mode={mode}
      headersFullWidthMode={headersFullWidthMode}
      headersCompressedMode={headersCompressedMode}
      tableData={tableData}
      disabled={isEditMode}
      title={I18n.t('dataExportPage.table.title')}
      entityType={I18n.t('entitiesTypes.dataExport')}
      selectedEntityIndex={selectedDataExportIndex}
      handleClickButtonNew={handleClickButtonNew}
      handleClickRow={handleClickRow}
      accessRights={rightAvailabilityMap}
    />
  );
}

export default DataExportTable;
